import React from 'react';
import { Alert, Button, Divider, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Spinner } from '../../../components/Spinner';
import { useAuthorRelationship } from '../../../core/modules/book/hooks/useAuthorRelationship';
import AuthorStatCards, { AuthorSyncStatus } from './AuthorStatCards';
import AuthorRelationshipSummary from './AuthorRelationshipSummary';
import AuthorDealsList from './AuthorDealsList';
import './author-relationship.css';

interface IOverlayAuthorRelationshipProps {
  authorName: string;
}

const OverlayAuthorRelationship: React.FC<IOverlayAuthorRelationshipProps> = ({ authorName }) => {
  const { data, isLoading, isError, refetch, refresh, isRefreshing } = useAuthorRelationship(authorName);

  if (!authorName) {
    return null;
  }

  const onRefresh = () =>
    refresh(undefined, {
      onError: () => message.error('Could not refresh HubSpot data. Please try again.')
    });

  if (isLoading) {
    return (
      <>
        <Divider className='overlay-section-divider' />
        <div className='ar-state'>
          <Spinner size='default' spinning indicator={<LoadingOutlined />} />
        </div>
      </>
    );
  }

  if (isError || !data) {
    return (
      <>
        <Divider className='overlay-section-divider' />
        <Alert
          type='warning'
          showIcon
          message='HubSpot deal history unavailable'
          description='We could not reach HubSpot just now.'
          action={<Button size='small' onClick={() => refetch()}>Retry</Button>}
        />
      </>
    );
  }

  if (!data.has_history) {
    return (
      <>
        <Divider className='overlay-section-divider' />
        <section className='ar-section'>
          <div className='ar-header'>
            <h3 className='overlay-section-title'>{authorName} stats at a glance</h3>
            <AuthorSyncStatus
              fetchedAt={data.fetched_at}
              isRefreshing={isRefreshing}
              onRefresh={onRefresh}
            />
          </div>
          <p className='ar-empty'>No HubSpot deals found for {authorName}.</p>
        </section>
      </>
    );
  }

  return (
    <>
      <Divider className='overlay-section-divider' />
      <section className='ar-section'>
        <div className='ar-header'>
          <h3 className='overlay-section-title'>{authorName} stats at a glance</h3>
          <AuthorSyncStatus
            fetchedAt={data.fetched_at}
            isRefreshing={isRefreshing}
            onRefresh={onRefresh}
          />
        </div>
        <AuthorStatCards stats={data.stats} />
        <AuthorRelationshipSummary authorName={authorName} dataVersion={data.fetched_at} />
      </section>
      <Divider className='overlay-section-divider' />
      <AuthorDealsList authorName={authorName} deals={data.deals} />
    </>
  );
};

export default OverlayAuthorRelationship;
